import http from "@/services/http";
import { getSourceId } from "@/utils/constants";

// 类目品牌数据指标 - 列表（按品牌聚合）

export interface BrandRatesItem {
  brandId: string;
  brandName: string;
  isCurBrand: boolean;
  mentionRate: number;
  firstMentionRate: number;
  firstRecommendationRate: number;
  positiveMentionRate: number;
  negativeMentionRate: number;
}

export interface BrandRatesResponse {
  code: number;
  message: string;
  data: {
    list: BrandRatesItem[];
    statisticsPeriodStart: string;
    statisticsPeriodEnd: string;
  };
}

export interface BrandRatesParams {
  brandId: string;
  categoryId: string;
  days?: number;
  startDay?: string; // 自定义开始日期，格式：2025-12-30
  endDay?: string; // 自定义结束日期，格式：2025-12-30
  modelId?: string;
  qId?: string; // 问题ID
  orderRate?:
    | "mentionRate"
    | "firstMentionRate"
    | "firstRecommendationRate"
    | "positiveMentionRate"
    | "negativeMentionRate";
  sourceId?: number; // 数据源ID：2=国内，3=国外
}

// 生成 10 条品牌指标（本地兜底/Mock 构造）
function buildMockBrandRates(
  params: BrandRatesParams
): BrandRatesResponse["data"] {
  const end = new Date();
  const start = new Date(end);
  const days = params.days ?? 1;
  start.setDate(end.getDate() - (days - 1));
  const toDateStr = (d: Date) => d.toISOString().split("T")[0];

  const names = [
    "比亚迪",
    "理想",
    "蔚来",
    "小米",
    "特斯拉",
    "广汽埃安",
    "上汽智己",
    "极氪",
    "问界",
    "哪吒",
  ];

  const list: BrandRatesItem[] = names.map((n, idx) => {
    const seed = (idx + 1) * 7;
    const rand = (min: number, max: number) =>
      Number((min + (max - min) * ((seed % 13) / 13)).toFixed(2));
    return {
      brandId:
        idx === 0 ? params.brandId : `${params.brandId || "brand"}-${idx}`,
      brandName: n,
      isCurBrand: idx === 0,
      mentionRate: rand(10, 40),
      firstMentionRate: rand(5, 25),
      firstRecommendationRate: rand(3, 20),
      positiveMentionRate: rand(60, 95),
      negativeMentionRate: rand(5, 25),
    };
  });

  // 排序（降序），默认按 mentionRate；可根据 orderRate 调整
  const key = params.orderRate || "mentionRate";
  list.sort((a, b) => (b as any)[key] - (a as any)[key]);

  return {
    list,
    statisticsPeriodStart: toDateStr(start),
    statisticsPeriodEnd: toDateStr(end),
  };
}

export const brandRatesApi = {
  async getBrandRates(
    params: BrandRatesParams
  ): Promise<BrandRatesResponse["data"]> {
    // 自动添加sourceId，如果没有指定则使用默认值
    const sourceId = params.sourceId ?? getSourceId();

    // 构建查询参数
    const queryParams = new URLSearchParams({
      brandId: params.brandId,
      categoryId: params.categoryId,
      days: String(params.days ?? 1),
      sourceId: sourceId.toString(),
    });
    if (params.modelId && params.modelId !== "all")
      queryParams.append("modelId", params.modelId);
    if (params.orderRate) queryParams.append("orderRate", params.orderRate);
    if (params.startDay) queryParams.append("startDay", params.startDay);
    if (params.endDay) queryParams.append("endDay", params.endDay);
    if (params.qId) queryParams.append("qId", params.qId);

    try {
      const res = await http.get<BrandRatesResponse | any>(
        `data/brand/rates?${queryParams.toString()}`
      );
      const payload = res?.data;
      if (
        payload &&
        payload.code === 200 &&
        payload.data &&
        Array.isArray(payload.data.list)
      ) {
        return payload.data as BrandRatesResponse["data"];
      }
      // 兜底：若结构异常，使用本地构造
      return buildMockBrandRates(params);
    } catch (e) {
      console.error("brandRatesApi.getBrandRates error:", e);
      // 失败兜底
      return buildMockBrandRates(params);
    }
  },
};

export default brandRatesApi;
